import * as React from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Heart, ShieldQuestion, Upload, Users2 } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@eduviaos/ui-kit";
import { userHasPermission, type TenantVerticalLiteral } from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { getVerticalProfile, permissionLabelFor } from "@/lib/verticalProfile";
import { ParentsTab } from "./ParentsTab";
import { StaffTab } from "./StaffTab";
import { StudentsTab } from "./StudentsTab";

/**
 * Phase J1 — `/people` 3-tab shell.
 *
 * Replaces the Phase D0c `<ModuleComingSoonPage />` stub that was
 * mounted at `/people`. The dashboard has been advertising the People
 * module as "Live" since D0c, but the route landed on a Coming-Soon
 * card — Phase J1 reconciles that drift.
 *
 * Tabs:
 *   - Students   (live in J1 + J1.b; consumes `/api/students/`)
 *   - Staff      (live in J2; consumes `/me/staff` gated on `view_staff`)
 *   - Parents    (live in J3; consumes `/api/parents/`)
 *
 * Phase F-finale-3 Slice 3 — the Parents tab is gated on the
 * `parent_portal` module. Tenants without `parent_portal` (e.g.
 * coaching/study-centre verticals that never enabled the module) get
 * a two-tab shell with no Parents trigger; deep links with
 * `?tab=parents` fall back to Students. The roster is hidden, not
 * 403'd — the data plane is still served by `view_students`, and
 * surfacing a "permission denied" card for a module the tenant
 * literally cannot enable would be misleading.
 *
 * Permission model:
 *   - Tab labels are always rendered so the shell stays predictable.
 *   - Reading the Students tab needs `view_students`. The Parents tab
 *     reads `/api/parents/` which the BE also guards on `view_students`
 *     (parent records are sensitive contact data — same blast radius
 *     as student PII; see `eduviaos-backend/app/domains/parents/
 *     routes.py`). A user without `view_students` sees a
 *     `PermissionDeniedCard` inside both the Students tab and the
 *     Parents tab. The Staff tab remains previewable because
 *     Coming-Soon copy is not sensitive.
 *   - Writes (J1.b for students, J3.b for parents) gate on
 *     `manage_students` per the same shared BE permission.
 *
 * Active tab is bound to the `?tab=` query string so refresh and
 * back-button preserve the user's last view. Default is `students`.
 */

type PeopleTab = "students" | "staff" | "parents";

const ALL_TABS: ReadonlyArray<PeopleTab> = ["students", "staff", "parents"];

function isPeopleTab(value: string | null): value is PeopleTab {
  return value !== null && (ALL_TABS as readonly string[]).includes(value);
}

export function PeoplePage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Phase F-finale-3 Slice 3 — module gate for the Parents tab.
  // We trust the enabled-modules payload (loaded once at app boot)
  // rather than the parent permission set; the tenant-vertical
  // catalogue is the source of truth for what's even available.
  const { isModuleEnabled, vertical } = useEnabledModules();
  const isLearningEnabled = isModuleEnabled("learning");
  const isParentPortalEnabled = isModuleEnabled("parent_portal");

  // SC Foundation (F0b) — de-school the People shell per the tenant's
  // vertical profile. A study centre has no parents/guardians and calls
  // its people "Learners" and its staff "Team"; a school keeps the
  // existing "People / Students / Staff" vocabulary byte-for-byte.
  const resolvedVertical: TenantVerticalLiteral = vertical ?? "school";
  const profile = getVerticalProfile(resolvedVertical);
  const isStudyCentre = vertical === "study_centre";
  const peopleHeading = isStudyCentre ? profile.personNoun.many : "People";
  const peopleDescription = isStudyCentre
    ? "Your learners — manage rosters, profiles, and bulk actions."
    : "Students, staff, parents, and guardians — manage rosters, profiles, and bulk actions.";
  const studentsTabLabel = profile.personNoun.many;
  const staffTabLabel = isStudyCentre ? "Team" : "Staff";
  const importLabel = `Import ${profile.personNoun.many.toLowerCase()}`;

  const showStaffTab = !isStudyCentre;

  const handleTabChange = React.useCallback(
    (next: string) => {
      if (!isPeopleTab(next)) return;
      const params = new URLSearchParams(searchParams);
      if (next === "students") {
        params.delete("tab");
      } else {
        params.set("tab", next);
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const canViewStudents = user
    ? userHasPermission(
        user.role,
        user.permission_overrides,
        "view_students",
        user.permission_mode ?? "additive",
      )
    : false;
  const canManageStudents = user
    ? userHasPermission(
        user.role,
        user.permission_overrides,
        "manage_students",
        user.permission_mode ?? "additive",
      )
    : false;
  const canImportStudents = user
    ? userHasPermission(
        user.role,
        user.permission_overrides,
        "import_students",
        user.permission_mode ?? "additive",
      )
    : false;
  const canViewStaff = user
    ? userHasPermission(
        user.role,
        user.permission_overrides,
        "view_staff",
        user.permission_mode ?? "additive",
      )
    : false;
  // Phase J2.d — write gate for staff suspend / reactivate row actions.
  // Distinct from `canManageStudents` so the BE's separate `manage_staff`
  // permission flows end-to-end without overloading the student gate.
  // Default-granted to ``admin`` only; owner/super_admin keep wildcard.
  const canManageStaff = user
    ? userHasPermission(
        user.role,
        user.permission_overrides,
        "manage_staff",
        user.permission_mode ?? "additive",
      )
    : false;
  const canManageAccess = user
    ? userHasPermission(
        user.role,
        user.permission_overrides,
        "manage_roles",
        user.permission_mode ?? "additive",
      )
    : false;

  const requested = searchParams.get("tab");
  const parsedTab: PeopleTab = isPeopleTab(requested) ? requested : "students";
  const activeTab: PeopleTab =
    parsedTab === "parents" && !isParentPortalEnabled ? "students" : parsedTab;
  // A study centre keeps team members off the learner roster. The old
  // `?tab=staff` link still resolves, onto the Team page.
  if (isStudyCentre && parsedTab === "staff") {
    return <Navigate to="/team" replace />;
  }

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>{peopleHeading}</PageHeaderHeading>
            <PageHeaderDescription>{peopleDescription}</PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            {canImportStudents ? (
              <Button asChild size="sm" variant="outline">
                <Link to="/imports/students">
                  <Upload aria-hidden="true" className="size-4" />
                  {importLabel}
                </Link>
              </Button>
            ) : null}
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList aria-label="People tabs">
          <TabsTrigger value="students" data-testid="people-tab-students">
            <Users2 aria-hidden="true" className="mr-1.5 size-3.5" />
            {studentsTabLabel}
          </TabsTrigger>
          {showStaffTab ? (
            <TabsTrigger value="staff" data-testid="people-tab-staff">
              <Users2 aria-hidden="true" className="mr-1.5 size-3.5" />
              {staffTabLabel}
            </TabsTrigger>
          ) : null}
          {isParentPortalEnabled ? (
            <TabsTrigger value="parents" data-testid="people-tab-parents">
              <Heart aria-hidden="true" className="mr-1.5 size-3.5" />
              Parents
            </TabsTrigger>
          ) : null}
        </TabsList>

        <TabsContent value="students" className="mt-4">
          {canViewStudents ? (
            <StudentsTab
              canManage={canManageStudents}
              tenantSlug={user?.tenant_id ?? null}
              vertical={vertical}
              learningModuleEnabled={isStudyCentre && isLearningEnabled}
            />
          ) : (
            <PermissionDeniedCard
              testId="people-students-permission-denied"
              missing="view_students"
              roster={profile.personNoun.many.toLowerCase()}
              vertical={resolvedVertical}
            />
          )}
        </TabsContent>

        {showStaffTab ? (
          <TabsContent value="staff" className="mt-4">
            {canViewStaff ? (
              <StaffTab
                canManage={canManageStudents}
                canManageStaff={canManageStaff}
                canManageAccess={canManageAccess}
                currentUserId={user?.id ?? null}
                tenantSlug={user?.tenant_id ?? null}
                vertical={resolvedVertical}
              />
            ) : (
              <PermissionDeniedCard
                testId="people-staff-permission-denied"
                missing="view_staff"
                roster={staffTabLabel.toLowerCase()}
                vertical={resolvedVertical}
              />
            )}
          </TabsContent>
        ) : null}

        {isParentPortalEnabled ? (
          <TabsContent value="parents" className="mt-4">
            {canViewStudents ? (
              <ParentsTab canManage={canManageStudents} tenantSlug={user?.tenant_id ?? null} />
            ) : (
              <PermissionDeniedCard
                testId="people-parents-permission-denied"
                missing="view_students"
                roster="parents and guardians"
                vertical={resolvedVertical}
              />
            )}
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
}

interface PermissionDeniedCardProps {
  testId: string;
  missing: string;
  /** Human label for what's behind the gate (e.g. "learners" or "parents and guardians"). */
  roster: string;
  vertical: TenantVerticalLiteral;
}

function PermissionDeniedCard({ testId, missing, roster, vertical }: PermissionDeniedCardProps) {
  const humanLabel = permissionLabelFor(vertical, missing);
  return (
    <Card className="border-border/80 bg-card/60" data-testid={testId}>
      <CardHeader className="flex-col items-start gap-3 pb-4">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="bg-muted text-muted-foreground grid size-12 place-items-center rounded-xl"
          >
            <ShieldQuestion className="size-6" />
          </span>
          <div>
            <CardTitle className="text-xl tracking-tight">No access yet</CardTitle>
            <CardDescription className="mt-1 text-xs">
              You don&apos;t have the &quot;{humanLabel}&quot; permission yet.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground text-sm leading-relaxed">
          Ask an admin to give you &quot;{humanLabel}&quot; to see the {roster} roster. You can
          still open the other tabs above.
        </p>
      </CardContent>
    </Card>
  );
}
